/* eslint-disable camelcase */

import { useState, useEffect, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { ComposeProviders, entities, modules } from "byzantine";
import { useNotificationContext } from "cerulean";
import { ResponsiveFlex, Drawer, Row, Button } from "@narmi/design_system";
import { SwiftDestinationFormProvider } from "byzantine/src/dbbl/businessLogic/entities/recipients/forms/SwiftDestinationFormProvider";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import styles from "./RecipientDrawer.module.scss";
import ProfileSection from "./ProfileSection";
import ACHDestinationSection from "./ACHDestinationSection";
import WireDestinationSection from "./WireDestinationSection";
import { DrawerValidationsProvider } from "./DrawerValidationsProvider";
import InternationalWireSection from "./InternationalWireSection";

type SectionNameProps = "profile" | "ach" | "wire" | "internationalWire";
type SectionNameStateType = SectionNameProps | "";
type SectionNamesMapperType = {
  [key in SectionNameProps]: SectionNameProps;
};
type DrawerTypeProps = "add" | "edit";
type DrawerTypesMapperType = {
  [key in DrawerTypeProps]: DrawerTypeProps;
};

export type InvoiceRecipientInitialValues = {
  name?: string | null;
  accountNumber?: string | null;
  routingNumber?: string | null;
  accountType?: string | null;
  type?: string | null;
};

export type RecipientDrawerProps = {
  isOpen: boolean;
  fromACHFlow?: boolean;
  fromDomesticWireFlow?: boolean;
  fromInternationalWireFlow?: boolean;
  handleClose: (recipientId?: API.RecipientId, rowIndex?: number) => void;
  recipientId?: API.RecipientId;
  drawerType: DrawerTypeProps;
  rowIndex?: number; // used when editing recipient from bulk payment table to know which row to update after editing
  customAlertCopy?: string; // used to pass in custom alert copy to show in ach section
  defaultSectionToOpen?: SectionNameProps; // pass in a section to default open when drawer opens
  needsReview?: boolean; // badge the profile/ACH sections as still needing the user's attention
  initialValuesFromInvoice?: InvoiceRecipientInitialValues; // seed the forms with values we already have (e.g. read off an invoice)
};

export type SectionProps = {
  title?: string;
  optionalLabel?: string;
  isOpen: boolean;
  // referring to when clicking "save" button or when clicking header to close section
  onClose: () => void;
  onOpen: () => void;
  // referring to when clicking on "save" button
  onSave: (recipientId?: API.RecipientId) => void;
  recipientId?: API.RecipientId;
  isEditDrawer?: boolean;
  needsReview?: boolean;
  // seeds for the underlying forms; every consumer of a form has to pass the
  // same ones, or whichever effect runs last resets the others' values away
  profileInitialValues?: { name?: string; type?: string };
  achInitialValues?: {
    accountNumber?: string;
    routingNumber?: string;
    accountType?: string;
  };
  fromDomesticWireFlow?: boolean;
  fromInternationalWireFlow?: boolean;
  customAlertCopy?: string;
};
export interface WireSectionProps extends SectionProps {
  savedACHDestination: boolean;
}

export const SECTION_NAMES: SectionNamesMapperType = {
  profile: "profile",
  ach: "ach",
  wire: "wire",
  internationalWire: "internationalWire",
};

export const DRAWER_TYPES: DrawerTypesMapperType = {
  add: "add",
  edit: "edit",
};

// Add or Edit Recipient Drawer
const RecipientDrawer = ({
  handleClose,
  isOpen,
  fromACHFlow,
  fromDomesticWireFlow,
  fromInternationalWireFlow,
  drawerType,
  recipientId,
  rowIndex,
  customAlertCopy,
  defaultSectionToOpen,
  needsReview,
  initialValuesFromInvoice,
}: RecipientDrawerProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const features = useUserFeatures();
  const { internationalWires } = modules.wireTemplates.useWirePermissions();
  const isEditDrawer = drawerType === DRAWER_TYPES.edit;
  const isAddDrawer = drawerType === DRAWER_TYPES.add;
  // in add drawer, always default open to profile section
  let defaultSection: SectionNameStateType = "";
  if (isAddDrawer) {
    defaultSection = SECTION_NAMES.profile;
  } else if (fromDomesticWireFlow) {
    defaultSection = SECTION_NAMES.wire;
  } else if (fromInternationalWireFlow) {
    defaultSection = SECTION_NAMES.internationalWire;
  } else if (defaultSectionToOpen) {
    defaultSection = defaultSectionToOpen;
  }

  const [enabledSection, setEnabledSection] =
    useState<SectionNameStateType>(defaultSection);
  // in add drawer, done button is default disabled
  const [doneButtonEnabled, setDoneButtonEnabled] =
    useState<boolean>(isEditDrawer);
  const [savedACHDestination, setSavedACHDestination] =
    useState<boolean>(false);
  const [savedWireDestination, setSavedWireDestination] =
    useState<boolean>(false);
  const [
    savedInternationalWireDestination,
    setSavedInternationalWireDestination,
  ] = useState<boolean>(false);
  const [addedRecipientInformation, setAddedRecipientInformation] =
    useState<boolean>(false);
  const [currentRecipientId, setCurrentRecipientId] = useState(recipientId);

  const enableSection = (section: SectionNameStateType) => {
    setEnabledSection(section);
  };

  const enableAllSections = () => {
    setEnabledSection("");
  };

  // handles toggling section logic
  // if user saves profile section in add drawer from ach flow, only open ach section
  // if user toggles any other section, enable all sections
  const handleClickSave = () => {
    setAddedRecipientInformation(true);

    if (enabledSection === SECTION_NAMES.ach) {
      setSavedACHDestination(true);
    } else if (enabledSection === SECTION_NAMES.wire) {
      setSavedWireDestination(true);
    } else if (enabledSection === SECTION_NAMES.internationalWire) {
      setSavedInternationalWireDestination(true);
    } else if (enabledSection === SECTION_NAMES.profile) {
      if (isAddDrawer && fromACHFlow) {
        enableSection(SECTION_NAMES.ach);
        return;
      }
      if (isAddDrawer && fromDomesticWireFlow) {
        enableSection(SECTION_NAMES.wire);
        return;
      }
      if (isAddDrawer && fromInternationalWireFlow) {
        enableSection(SECTION_NAMES.internationalWire);
        return;
      }
    }

    enableAllSections();
  };

  const handleSaveProfileSection = (
    newlyCreatedRecipientId?: API.RecipientId
  ) => {
    setCurrentRecipientId(newlyCreatedRecipientId);
    handleClickSave();
  };

  useEffect(() => {
    if (enabledSection) {
      setDoneButtonEnabled(false);
    } else if (!enabledSection) {
      if (isEditDrawer) {
        setDoneButtonEnabled(true);
      } else if (isAddDrawer && fromACHFlow && savedACHDestination) {
        setDoneButtonEnabled(true);
      } else if (isAddDrawer && fromDomesticWireFlow && savedWireDestination) {
        setDoneButtonEnabled(true);
      } else if (
        isAddDrawer &&
        !fromDomesticWireFlow &&
        savedInternationalWireDestination
      ) {
        setDoneButtonEnabled(true);
      } else if (
        isAddDrawer &&
        !fromACHFlow &&
        !fromDomesticWireFlow &&
        (savedACHDestination || savedWireDestination)
      ) {
        setDoneButtonEnabled(true);
      }
    }
  }, [enabledSection]);

  useEffect(() => {
    setEnabledSection(defaultSection);
  }, [defaultSection]);

  useEffect(() => {
    if (isEditDrawer && fromInternationalWireFlow) {
      enableSection("internationalWire");
    } else if (isEditDrawer && fromDomesticWireFlow) {
      enableSection("wire");
    } else if (isAddDrawer) {
      enableSection("profile");
    }
  }, [drawerType, recipientId]);

  useEffect(() => {
    // every time drawer is opened, reset the recipientId
    if (isOpen) {
      setCurrentRecipientId(recipientId);
    }
  }, [isOpen]);

  const resetDrawer = () => {
    // reset to state defaults
    enableSection(isAddDrawer ? SECTION_NAMES.profile : "");
    setDoneButtonEnabled(isEditDrawer);
    setSavedACHDestination(false);
    setSavedWireDestination(false);
    setSavedInternationalWireDestination(false);
    setAddedRecipientInformation(false);
    if (isAddDrawer) {
      setCurrentRecipientId(undefined);
    }
  };

  const handleCloseDrawer = () => {
    resetDrawer();

    if (rowIndex !== undefined) {
      handleClose(currentRecipientId, rowIndex);
    } else {
      handleClose(currentRecipientId);
    }

    if (addedRecipientInformation) {
      sendNotification({
        type: "success",
        text: isAddDrawer
          ? "Recipient saved."
          : "Recipient information updated.",
      });
    }
  };

  const getWireSectionHeader = useMemo(() => {
    const title = l10n.getString("recipient-drawer-section-domestic-wire");
    if (
      (fromACHFlow || fromInternationalWireFlow) &&
      !savedWireDestination &&
      !fromDomesticWireFlow
    ) {
      return {
        title,
        optionalLabel: l10n.getString("recipient-drawer-section-optional"),
      };
    }
    return { title };
  }, [
    fromACHFlow,
    fromInternationalWireFlow,
    savedWireDestination,
    fromDomesticWireFlow,
    l10n,
  ]);
  const getACHSectionHeader = useMemo(() => {
    const title = l10n.getString("recipient-drawer-section-ach");
    if (fromInternationalWireFlow && !savedACHDestination) {
      return {
        title,
        optionalLabel: l10n.getString("recipient-drawer-section-optional"),
      };
    }
    return { title };
  }, [fromInternationalWireFlow, savedACHDestination, l10n]);

  const getInternationalWireSectionHeader = useMemo(() => {
    const title = l10n.getString("recipient-drawer-section-international-wire");
    if (fromACHFlow && !savedInternationalWireDestination) {
      return {
        title,
        optionalLabel: l10n.getString("recipient-drawer-section-optional"),
      };
    }
    return { title };
  }, [fromACHFlow, savedInternationalWireDestination, l10n]);

  const getDrawerSubheader = () => {
    if (fromInternationalWireFlow) {
      return l10n.getString("add-international-wire-recipient-drawer-subtitle");
    }
    if (isAddDrawer) {
      return l10n.getString("add-recipient-subtitle-details");
    }
    return l10n.getString("edit-recipient-drawer-subtitle");
  };

  // The forms reset to these whenever there is no saved recipient, so values we
  // already have (e.g. read off an invoice) survive the reset instead of racing
  // it. A saved recipient still wins — those resets use its own data.
  // Memoised because the hooks take these as effect dependencies.
  const invoiceName = initialValuesFromInvoice?.name;
  const invoiceAccountNumber = initialValuesFromInvoice?.accountNumber;
  const invoiceRoutingNumber = initialValuesFromInvoice?.routingNumber;
  const invoiceAccountType = initialValuesFromInvoice?.accountType;
  const invoiceRecipientType = initialValuesFromInvoice?.type;

  // only while creating: never seed a name over a saved recipient's own
  const profileInitialValues = useMemo(
    () =>
      isAddDrawer && (invoiceName || invoiceRecipientType)
        ? {
            name: invoiceName || "",
            type: invoiceRecipientType || "",
          }
        : undefined,
    [isAddDrawer, invoiceName, invoiceRecipientType]
  );
  const achInitialValues = useMemo(
    () =>
      invoiceAccountNumber || invoiceRoutingNumber || invoiceAccountType
        ? {
            accountNumber: invoiceAccountNumber || "",
            routingNumber: invoiceRoutingNumber || "",
            accountType: invoiceAccountType || "",
          }
        : undefined,
    [invoiceAccountNumber, invoiceRoutingNumber, invoiceAccountType]
  );

  const mapPaymentSections = {
    ach: (
      <ACHDestinationSection
        key="ach_destination"
        title={getACHSectionHeader.title}
        optionalLabel={getACHSectionHeader.optionalLabel}
        isOpen={enabledSection === SECTION_NAMES.ach}
        onClose={enableAllSections}
        onOpen={() => enableSection(SECTION_NAMES.ach)}
        onSave={handleClickSave}
        recipientId={currentRecipientId}
        customAlertCopy={customAlertCopy}
        needsReview={needsReview}
        achInitialValues={achInitialValues}
      />
    ),
    wire: (
      <WireDestinationSection
        key="wire_destination"
        title={getWireSectionHeader.title}
        optionalLabel={getWireSectionHeader.optionalLabel}
        isOpen={enabledSection === SECTION_NAMES.wire}
        onClose={enableAllSections}
        onOpen={() => enableSection(SECTION_NAMES.wire)}
        savedACHDestination={savedACHDestination}
        onSave={handleClickSave}
        recipientId={currentRecipientId}
        fromDomesticWireFlow={fromDomesticWireFlow}
        achInitialValues={achInitialValues}
      />
    ),
    internationalWire: (
      <InternationalWireSection
        key="international_wire_destination"
        title={getInternationalWireSectionHeader.title}
        optionalLabel={getInternationalWireSectionHeader.optionalLabel}
        isOpen={enabledSection === SECTION_NAMES.internationalWire}
        onClose={enableAllSections}
        onOpen={() => enableSection(SECTION_NAMES.internationalWire)}
        onSave={handleClickSave}
        recipientId={currentRecipientId}
        isEditDrawer={isEditDrawer}
        fromInternationalWireFlow={fromInternationalWireFlow}
      />
    ),
  };

  const sectionComponents = {
    ach: features?.ach_payments ? mapPaymentSections.ach : null,
    wire: features?.wires ? mapPaymentSections.wire : null,
    internationalWire: internationalWires
      ? mapPaymentSections.internationalWire
      : null,
  };

  const getSectionOrder = (): Array<keyof typeof sectionComponents> => {
    if (fromACHFlow) {
      return ["ach", "wire", "internationalWire"];
    }
    if (fromInternationalWireFlow) {
      return ["internationalWire", "ach", "wire"];
    }
    return ["ach", "wire", "internationalWire"];
  };

  const getOrderedPaymentSections = () => (
    <>
      {getSectionOrder()
        .map((key) => sectionComponents[key])
        .filter(Boolean)}
    </>
  );

  const Providers = [
    entities.recipients.RecipientProfileForm.Provider,
    entities.recipients.ACHDestinationForm.Provider,
    entities.recipients.WireDestinationForm.Provider,
    SwiftDestinationFormProvider,
  ];

  return (
    <ComposeProviders components={Providers}>
      <Drawer
        depth="633px"
        isOpen={isOpen}
        onUserDismiss={handleCloseDrawer}
        showControls={false}
        paddingSize="xl"
        footer={
          <Row alignItems="center" justifyContent="space-between">
            <Row.Item shrink>
              <Button
                kind="negative"
                testId="cancel-recipient-drawer"
                onClick={handleCloseDrawer}
                label={l10n.getString("button-cancel")}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                testId="done-recipient-drawer"
                onClick={handleCloseDrawer}
                label={l10n.getString("recipient-drawer-done-button")}
                disabled={!doneButtonEnabled}
              />
            </Row.Item>
          </Row>
        }
      >
        <DrawerValidationsProvider
          drawerType={drawerType}
          recipientId={recipientId}
          profileInitialValues={profileInitialValues}
          achInitialValues={achInitialValues}
        >
          <div className={styles.drawerContainer}>
            <ResponsiveFlex gapSize="s">
              <h2>
                {l10n.getString(
                  isAddDrawer
                    ? "add-recipient-drawer-title"
                    : "edit-recipient-drawer-title"
                )}
              </h2>
              <p>{getDrawerSubheader()}</p>
              <ProfileSection
                isOpen={enabledSection === SECTION_NAMES.profile}
                onClose={enableAllSections}
                onOpen={() => enableSection(SECTION_NAMES.profile)}
                onSave={handleSaveProfileSection}
                recipientId={currentRecipientId}
                needsReview={needsReview}
                profileInitialValues={profileInitialValues}
              />
              {getOrderedPaymentSections()}
            </ResponsiveFlex>
          </div>
        </DrawerValidationsProvider>
      </Drawer>
    </ComposeProviders>
  );
};

export default RecipientDrawer;
